import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { devices, type Zariadenie } from '../api/client.js';
import { useApi } from '../api/use-api.js';
import { EmptyState } from '../components/states.js';
import { Badge, Button, Card, PageHeader } from '../components/ui.js';
import { IconBattery, IconChevronRight, IconRefresh } from '../components/icons.js';
import { Data, StavZariadenia, nazovSchopnosti } from './common.js';
import {
  PanelOvladania,
  meranie,
  stavSenzora,
  stavoveSchopnosti,
  useOvladanie,
} from './controls.js';

/*
 * Zoznam zariadeni.
 *
 * Dlazdica, nie riadok tabulky: na dlazdici sa da hned prepnut svetlo
 * bez otvarania detailu, co je najcastejsia vec, ktoru tu clovek robi.
 * Podrobnosti a posuvniky su az v detaile.
 */

function Dlazdica({ device }: { readonly device: Zariadenie }): ReactNode {
  const ovladanie = useOvladanie(device);
  const schopnosti = device.capabilities.map((c) => nazovSchopnosti(c.name)).join(' · ');

  return (
    <Card as="li" className="flex h-full flex-col gap-3 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <Link
            to={`/zariadenia/${device.id}`}
            className="truncate font-medium text-(--color-text) hover:text-(--color-accent)"
          >
            {device.name}
          </Link>
          <StavZariadenia availability={device.availability} lastMessageAt={device.lastMessageAt} />
        </div>
        <PanelOvladania device={device} ovladanie={ovladanie} compact />
      </div>

      <StavDlazdice device={device} />

      {/* `mt-auto` drzi spodny riadok pri dne, nech je obsah akykolvek. */}
      <div className="mt-auto flex items-end justify-between gap-3">
        <p className="min-w-0 truncate text-xs text-(--color-text-subtle)">{schopnosti}</p>
        <Link
          to={`/zariadenia/${device.id}`}
          aria-label={`Detail zariadenia ${device.name}`}
          className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-(--color-accent)"
        >
          Detail
          <IconChevronRight className="size-3.5" />
        </Link>
      </div>
    </Card>
  );
}

export function Devices(): ReactNode {
  const [hladanie, setHladanie] = useState('');
  const nacitanie = useApi((signal) => devices.list(signal));

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Zariadenia"
        detail="Všetko, čo brána našla cez svoje integrácie."
        action={
          <Button
            onClick={nacitanie.reload}
            icon={<IconRefresh className="size-4" />}
            variant="ghost"
          >
            Obnoviť
          </Button>
        }
      />

      <Data nacitanie={nacitanie}>
        {(data) => {
          if (data.devices.length === 0) {
            return (
              <EmptyState
                title="Zatiaľ žiadne zariadenia"
                detail="Objavia sa tu samy, keď ich nájde niektorá integrácia."
              />
            );
          }

          const hladane = hladanie.trim().toLowerCase();
          const zobrazene =
            hladane === ''
              ? data.devices
              : data.devices.filter(
                  (d) =>
                    d.name.toLowerCase().includes(hladane) ||
                    d.integrationId.toLowerCase().includes(hladane),
                );

          return (
            <div className="flex flex-col gap-4">
              <input
                type="search"
                value={hladanie}
                onChange={(e) => setHladanie(e.target.value)}
                placeholder="Hľadať podľa názvu alebo integrácie"
                aria-label="Hľadať zariadenie"
                className="w-full rounded-(--radius-control) border border-(--color-border-strong) bg-(--color-surface-raised) px-3 py-2 text-sm text-(--color-text) placeholder:text-(--color-text-subtle) sm:max-w-sm"
              />

              {zobrazene.length === 0 ? (
                <EmptyState
                  title="Nič sa nenašlo"
                  detail={`Výrazu „${hladanie}" nezodpovedá žiadne zariadenie.`}
                />
              ) : (
                <>
                  {/*
                   * `auto-rows-fr` zrovna vysku VSETKYCH riadkov, nie len
                   * dlazdic vedla seba. Zariadenie so senzorom ma o riadok
                   * viac nez obycajna ziarovka a mriezka inak vyzerala
                   * ako schodisko.
                   *
                   * `grid-cols-1` nie je zbytocne: bez neho ma mriezka na
                   * mobile implicitny stlpec `auto`, teda siroky aspon ako
                   * obsah - a dlhy zoznam schopnosti sa potom neskratil,
                   * ale roztiahol dlazdicu za okraj. `sm:grid-cols-2` to
                   * na desktope robi spravne (`minmax(0, 1fr)`), preto tam
                   * chyba nebola vidno (hlasene 2026-09-18).
                   */}
                  <ul className="grid grid-cols-1 auto-rows-fr gap-3 sm:grid-cols-2">
                    {zobrazene.map((d) => (
                      <Dlazdica key={d.id} device={d} />
                    ))}
                  </ul>
                </>
              )}
            </div>
          );
        }}
      </Data>
    </div>
  );
}

/**
 * Stav senzora priamo na dlaždici.
 *
 * Senzor prítomnosti ani okenný kontakt sa nedajú ovládať, takže na
 * dlaždici dovtedy nebolo nič — a práve to je pritom jediný údaj, kvôli
 * ktorému sa na taký zoznam človek pozerá.
 */
function StavDlazdice({ device }: { readonly device: Zariadenie }): ReactNode {
  const stavy = stavoveSchopnosti(device)
    .map((name) => ({ name, stav: stavSenzora(device, name) }))
    .filter((s): s is { name: string; stav: NonNullable<typeof s.stav> } => s.stav !== undefined);

  const akcia = device.lastAction;
  const surovaBateria = device.state['Battery'];
  const bateria = typeof surovaBateria === 'number' ? Math.round(surovaBateria) : undefined;
  /*
   * Hlavne cislo na dlazdici.
   *
   * Zariadenie pocasia sa inak v zozname nicim neprejavi - nema co
   * ovladat ani ziadny zapnuty/vypnuty stav, takze dlazdica bola prazdna
   * (ziadost zadavatela 2026-10-10). Teplota a stav oblohy su to, kvoli
   * comu sa na nu clovek pozera.
   */
  const teplota = meranie(device, 'Temperature');
  const obloha = meranie(device, 'WeatherCode');
  if (
    stavy.length === 0 &&
    akcia === null &&
    bateria === undefined &&
    teplota === undefined &&
    obloha === undefined
  ) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {stavy.map((s) => (
        <Badge key={s.name} tone={s.stav.ton}>
          {s.stav.popis}
        </Badge>
      ))}
      {teplota !== undefined && (
        <span className="text-sm font-medium text-(--color-text)">{teplota.popis}</span>
      )}
      {obloha !== undefined && <Badge tone="neutral">{obloha.popis}</Badge>}
      {akcia !== null && <Badge tone="accent">{akcia.value}</Badge>}

      {/*
       * Bateria a signal su diagnostika: patria na dlazdicu male
       * a potichu, aby sa dali prebehnut ocami cez cely zoznam, ale
       * neprekricali to, kvoli comu sa na zariadenie clovek pozera.
       */}
      {bateria !== undefined && (
        <span
          className="inline-flex items-center gap-1 text-xs text-(--color-text-subtle)"
          title={`Batéria ${bateria} %`}
        >
          <IconBattery percent={bateria} className="size-4" />
          {bateria} %
        </span>
      )}
    </div>
  );
}
